import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { useNotification } from 'config/Notification';
import {
    fetchCompanyStandardsByCompany as fetchCompanyStandardsByCompanyService,
    fetchControlsByCompanyStandardId as fetchControlsByCompanyStandardIdService,
    updateCompanyControlsweightages as updateCompanyControlsWeightagesService,
} from 'services/modules/cap/standardservice';
import Breadcrumbs from 'config/Breadcrumb';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Determines the health status of a control for weightage purposes.
 *
 * Returns one of:
 *   'inactive'        – control.status !== true → locked, 0 weight forced
 *   'no_questions'    – no active subcontrols → locked, 0 weight forced
 *   'all_info'        – all active questions are informational → locked, 0 weight forced
 *   'high_info'       – >50% active questions informational → warning, editable
 *   'ok'              – normal, editable
 */
const getControlHealth = (control) => {
    const stats = control._subcontrolStats;

    if (!control.status) return 'inactive';
    if (!stats) return 'ok'; // no stats yet — allow edit

    if (stats.active_subcontrols === 0) return 'no_questions';
    if (stats.scored_subcontrols === 0) return 'all_info';

    const infoRatio = stats.active_subcontrols > 0
        ? stats.informational_subcontrols / stats.active_subcontrols
        : 0;

    if (infoRatio > 0.5) return 'high_info';
    return 'ok';
};

const HEALTH_CONFIG = {
    inactive: {
        label:   'Inactive control',
        color:   'default',
        icon:    <LockOutlinedIcon sx={{ fontSize: 14 }} />,
        tooltip: 'This control is inactive and cannot receive a weightage.',
        blocked: true,
    },
    no_questions: {
        label:   'No active questions',
        color:   'error',
        icon:    <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
        tooltip: 'This control has no active sub-controls or questions. Add active questions before assigning a weightage.',
        blocked: true,
    },
    all_info: {
        label:   'All questions informational',
        color:   'error',
        icon:    <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
        tooltip: 'Every active question in this control is marked informational and will never be scored. Assigning a weightage has no effect.',
        blocked: true,
    },
    high_info: {
        label:   'High informational ratio',
        color:   'warning',
        icon:    <WarningAmberIcon sx={{ fontSize: 14 }} />,
        tooltip: 'More than 50% of active questions are informational. The scored portion of this control will be smaller than its weightage suggests.',
        blocked: false,
    },
    ok: {
        label:   null,
        color:   'success',
        icon:    <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
        tooltip: 'All active questions contribute to the score.',
        blocked: false,
    },
};

// ─── ControlHealthBadge ───────────────────────────────────────────────────────

const ControlHealthBadge = ({ control }) => {
    const health = getControlHealth(control);
    const cfg = HEALTH_CONFIG[health];
    if (!cfg || health === 'ok') return null;

    return (
        <Tooltip title={cfg.tooltip} placement="right" arrow>
            <Chip
                icon={cfg.icon}
                label={cfg.label}
                color={cfg.color}
                size="small"
                variant="outlined"
                sx={{ ml: 1, fontSize: '11px', height: 22 }}
            />
        </Tooltip>
    );
};

// ─── InformationalWarningPanel ────────────────────────────────────────────────

const InformationalWarningPanel = ({ controls }) => {
    const warnings = controls.filter(c => {
        const h = getControlHealth(c);
        return h === 'high_info' || h === 'all_info';
    });

    if (warnings.length === 0) return null;

    return (
        <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2 }}
        >
            <strong>Informational question imbalance detected:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                {warnings.map(c => {
                    const stats = c._subcontrolStats;
                    const health = getControlHealth(c);
                    return (
                        <li key={c._id} style={{ fontSize: 13 }}>
                            <strong>{c.Title}</strong>
                            {health === 'all_info'
                                ? ' — ALL active questions are informational. This control cannot be scored.'
                                : ` — ${stats?.informational_subcontrols} of ${stats?.active_subcontrols} active questions are informational (${Math.round((stats?.informational_subcontrols / stats?.active_subcontrols) * 100)}%).`
                            }
                        </li>
                    );
                })}
            </ul>
        </Alert>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const ConfigureControlsWeightages = () => {
    const standardTypes = [
        { id: 'domain-based', name: 'Domain Based' }
    ];

    const [standardType, setStandardType] = useState(standardTypes[0].id);
    const [companyId, setCompanyId] = useState(null);
    const [selectedStandards, setSelectedStandards] = useState([]);
    const [controls, setControls] = useState([]);
    const [selectedStandard, setSelectedStandard] = useState('');

    const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
    const { openNotification } = useNotification();

    // Eligible controls: active + has at least one scored (non-informational) question
    const eligibleControls = useMemo(() =>
        controls.filter(c => getControlHealth(c) !== 'inactive'
                           && getControlHealth(c) !== 'no_questions'
                           && getControlHealth(c) !== 'all_info'),
        [controls]
    );

    const blockedControls = useMemo(() =>
        controls.filter(c => ['inactive','no_questions','all_info'].includes(getControlHealth(c))),
        [controls]
    );

    const fetchStandardsData = async () => {
        try {
            let group = standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard';
            const response = await fetchCompanyStandardsByCompanyService({ group, companyId });
            setSelectedStandards(response.data);
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        }
    };

    const fetchControlsDataByStandard = async (standard) => {
        try {
            setControls([]);
            const params = { Standard: standard, company: companyId };
            const response = await fetchControlsByCompanyStandardIdService(params);
            setControls(response.data || []);
        } catch (error) {
            openNotification("Error while fetching controls", 'error');
        }
    };

    // Validates only eligible controls sum to 100
    const validateTotalWeightage = (weightages) => {
        const eligibleIds = new Set(eligibleControls.map(c => c._id || c.id));
        const total = weightages
            .filter(w => eligibleIds.has(w.id))
            .reduce((acc, curr) => acc + parseFloat(curr.weight || 0), 0);
        const rounded = Math.round(total * 100) / 100;

        if (rounded < 100) return `Total weightage of eligible controls is ${rounded}% — must be exactly 100%.`;
        if (rounded > 100) return `Total weightage of eligible controls is ${rounded}% — must not exceed 100%.`;
        return null;
    };

    const validationSchemaStep2 = Yup.object({
        standard: Yup.object({
            id: Yup.string().required('Standard ID is required'),
            name: Yup.string().required('Standard Name is required'),
        }).required('Standard is required'),
        weightages: Yup.array().of(
            Yup.object().shape({
                id:     Yup.string().required('Required'),
                weight: Yup.number()
                    .required('Required')
                    .min(0, 'Min value is 0')
                    .max(100, 'Max value is 100')
                    .test('is-decimal', 'Must be a valid decimal', v => /^\d+(\.\d{1,2})?$/.test(v)),
            })
        ),
    });

    const formikStep2 = useFormik({
        initialValues: {
            standard:  selectedStandard,
            weightages: controls.map((c) => ({
                id:        c._id || c.id,
                controlID: c.ControlID,
                control:   c.Title,
                weight:    c.Weightage || 0,
            })),
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            const error = validateTotalWeightage(values.weightages);
            if (error) {
                openNotification(error, 'error');
                return;
            }
            try {
                // Only send eligible controls — blocked controls get weight 0
                const blockedIds = new Set(blockedControls.map(c => c._id || c.id));
                const bodylist = values.weightages.map((e) => ({
                    id:        e.id,
                    ControlID: e.controlID,
                    Weightage: blockedIds.has(e.id) ? 0 : e.weight,
                }));

                const response = await updateCompanyControlsWeightagesService(
                    values.standard.id,
                    { body: bodylist }
                );
                if (response.status === 200) {
                    openNotification("Control weightages updated successfully", 'success');
                    fetchControlsDataByStandard(selectedStandard.stdid);
                }
            } catch (error) {
                openNotification("Error while updating control weightages", 'error');
            }
        },
        enableReinitialize: true,
    });

    const handleStandardChange = (event) => {
        const standard = event.target.value;
        setSelectedStandard(standard);
        formikStep2.setFieldValue('standard', standard);
    };

    useEffect(() => {
        if (selectedCompanyContainer) setCompanyId(selectedCompanyContainer.companyid);
    }, [selectedCompanyContainer]);

    useEffect(() => {
        if (companyId) {
            setControls([]);
            fetchStandardsData();
        }
    }, [standardType, companyId]);

    useEffect(() => {
        if (selectedStandard) fetchControlsDataByStandard(selectedStandard.stdid);
    }, [selectedStandard]);

    // Live total of eligible controls only
    const liveTotal = useMemo(() => {
        const eligibleIds = new Set(eligibleControls.map(c => c._id || c.id));
        return Math.round(
            formikStep2.values.weightages
                .filter(w => eligibleIds.has(w.id))
                .reduce((acc, w) => acc + parseFloat(w.weight || 0), 0) * 100
        ) / 100;
    }, [formikStep2.values.weightages, eligibleControls]);

    const totalColor = liveTotal === 100 ? 'success' : liveTotal > 100 ? 'error' : 'warning';

    return (
        <div className='main-cont'>
            <Card>
                <CardHeader
                    title={
                        <div className="card-head">
                            <div className="flex-head">
                                <label className="label">
                                    <Breadcrumbs paths={['CAP Configurator', 'Configure Control Weightages']} />
                                </label>
                            </div>
                            <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                                Back
                            </Button>
                        </div>
                    }
                />
                <CardContent>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Select Standard Type</FormLabel>
                        <RadioGroup
                            value={standardType}
                            onChange={(e) => setStandardType(e.target.value)}
                            className='d-flex flex-row gap-2'
                        >
                            {standardTypes.map((type) => (
                                <FormControlLabel key={type.id} value={type.id} control={<Radio />} label={type.name} />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <Divider className='my-1' />

                    {standardType && (
                        <CardContent className='loader-container'>
                            <form onSubmit={formikStep2.handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Standard</InputLabel>
                                            <Select
                                                name="standard"
                                                label="Standard"
                                                value={formikStep2.values.standard}
                                                onChange={handleStandardChange}
                                                error={formikStep2.touched.standard && Boolean(formikStep2.errors.standard)}
                                            >
                                                {selectedStandards.map((s) => (
                                                    <MenuItem key={s.id} value={s}>{s.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {controls.length > 0 && (
                                    <>
                                        {/* Informational imbalance warnings */}
                                        <InformationalWarningPanel controls={controls} />

                                        {/* Blocked controls info */}
                                        {blockedControls.length > 0 && (
                                            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                                                <strong>{blockedControls.length} control{blockedControls.length > 1 ? 's are' : ' is'} excluded from weightage:</strong>
                                                {' '}{blockedControls.map(c => c.Title).join(', ')}.
                                                {' '}These will be saved with weightage 0.
                                                {' '}The remaining {eligibleControls.length} eligible control{eligibleControls.length !== 1 ? 's' : ''} must total 100%.
                                            </Alert>
                                        )}

                                        {/* Live total indicator */}
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <Typography variant="body2" color="text.secondary">
                                                Eligible controls total:
                                            </Typography>
                                            <Chip
                                                label={`${liveTotal}% / 100%`}
                                                color={totalColor}
                                                size="small"
                                                variant={liveTotal === 100 ? 'filled' : 'outlined'}
                                            />
                                        </Box>

                                        {/* Control weightage rows */}
                                        {formikStep2.values.weightages.map((weightage, index) => {
                                            const control = controls.find(c => (c._id || c.id) === weightage.id);
                                            const health  = control ? getControlHealth(control) : 'ok';
                                            const cfg     = HEALTH_CONFIG[health];
                                            const blocked = cfg.blocked;
                                            const stats   = control?._subcontrolStats;

                                            return (
                                                <Box
                                                    key={weightage.id}
                                                    mb={2}
                                                    p={1.5}
                                                    sx={{
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: blocked
                                                            ? 'error.light'
                                                            : health === 'high_info'
                                                            ? 'warning.light'
                                                            : 'divider',
                                                        background: blocked ? '#fff5f5' : health === 'high_info' ? '#fffbf0' : 'transparent',
                                                        opacity: blocked ? 0.75 : 1,
                                                    }}
                                                >
                                                    <Grid container spacing={2} alignItems="center">
                                                        {/* Control name + health badge */}
                                                        <Grid item xs={5}>
                                                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                                                                <Typography variant="body1" color={blocked ? 'text.disabled' : 'text.primary'}>
                                                                    {weightage.control}
                                                                </Typography>
                                                                <ControlHealthBadge control={control} />
                                                            </Box>
                                                            {/* Sub-control stats */}
                                                            {stats && (
                                                                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                                                                    {stats.active_subcontrols} active question{stats.active_subcontrols !== 1 ? 's' : ''}
                                                                    {stats.informational_subcontrols > 0 && (
                                                                        <span style={{ color: health === 'all_info' ? '#c62828' : '#f57c00' }}>
                                                                            {' '}· {stats.informational_subcontrols} informational
                                                                        </span>
                                                                    )}
                                                                    {stats.scored_subcontrols > 0 && (
                                                                        <span style={{ color: '#2e7d32' }}>
                                                                            {' '}· {stats.scored_subcontrols} scored
                                                                        </span>
                                                                    )}
                                                                </Typography>
                                                            )}
                                                        </Grid>

                                                        {/* Weight input */}
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                label="Weight %"
                                                                name={`weightages[${index}].weight`}
                                                                disabled={blocked || standardType === 'standard-based'}
                                                                value={blocked ? 0 : formikStep2.values.weightages[index]?.weight}
                                                                onChange={formikStep2.handleChange}
                                                                error={
                                                                    !blocked &&
                                                                    formikStep2.touched.weightages?.[index]?.weight &&
                                                                    Boolean(formikStep2.errors.weightages?.[index]?.weight)
                                                                }
                                                                helperText={
                                                                    !blocked &&
                                                                    formikStep2.touched.weightages?.[index]?.weight &&
                                                                    formikStep2.errors.weightages?.[index]?.weight
                                                                }
                                                                fullWidth
                                                                margin="normal"
                                                                inputProps={{ step: '0.1', min: 0, max: 100 }}
                                                                sx={{ '& .MuiInputBase-root': { background: blocked ? '#f5f5f5' : undefined } }}
                                                            />
                                                        </Grid>

                                                        {/* Locked reason for blocked */}
                                                        {blocked && (
                                                            <Grid item xs={4}>
                                                                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                                                                    <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                                    <Typography variant="caption" color="text.disabled">
                                                                        {cfg.tooltip}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            );
                                        })}

                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                type="submit"
                                                disabled={standardType === 'standard-based'}
                                            >
                                                Save Weightages
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </form>
                        </CardContent>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ConfigureControlsWeightages;